import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save, Upload, RefreshCw, Check, X,
  Edit3, Lock, Unlock, Users, IndianRupee, AlertCircle,
  FileSpreadsheet, TrendingUp, Eye, Filter, Search, PauseCircle, ChevronDown,
  Plus, Trash2, Download, UploadCloud, Edit, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import * as XLSX from 'xlsx';
import { buildApiUrl } from '../../utils/apiSetup';

const PayrollEditor = ({ runId, onClose, hideApprovalActions = false }) => {
  const { hasPermission } = useAuth();
  const { user } = useAuth();
  const canEdit = hasPermission('payroll:edit') || hasPermission('*');
  const canApprove = hasPermission('payroll:approve') || hasPermission('*');
  const canDelete = hasPermission('payroll:delete') || hasPermission('*');
  const canExport = hasPermission('payroll:export') || hasPermission('*');
  const canImport = hasPermission('payroll:import') || hasPermission('*');

  const canApprovedEdit = hasPermission('payroll:approved:edit') || hasPermission('*');
  const canApprovedReject = hasPermission('payroll:approved:reject') || hasPermission('*');

  const [isApprovedEditMode, setIsApprovedEditMode] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [approvedEditChanges, setApprovedEditChanges] = useState({});
  const [showOverrideReasonModal, setShowOverrideReasonModal] = useState(false);
  const [dbOverrides, setDbOverrides] = useState({});

  const [payrollData, setPayrollData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showSuccess, showError } = useNotification();
  const [saving, setSaving] = useState(false);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [showCalculations, setShowCalculations] = useState(true); // Always show totals row
  const [calculatedDeductions, setCalculatedDeductions] = useState({});
  const [banks, setBanks] = useState([]);

  // Payroll Modification Deadline (5th of following calendar month at 11:59:59 PM)
  const isPayrollLocked = useMemo(() => {
    if (!payrollData || payrollData.status !== 'approved') return false;

    // Use backend's is_locked if available
    if (typeof payrollData.is_locked === 'boolean') {
      return payrollData.is_locked;
    }

    const month = payrollData.month || payrollData.payroll_month;
    const year = payrollData.year || payrollData.payroll_year;
    if (!month || !year) return false;

    let followingYear = year;
    let followingMonth = month + 1;
    if (month === 12) {
      followingYear = year + 1;
      followingMonth = 1;
    }

    // Inclusive deadline: 5th day 23:59:59.999
    const deadline = new Date(followingYear, followingMonth - 1, 5, 23, 59, 59, 999);
    return new Date() > deadline;
  }, [payrollData]);

  const deadlineDisplay = useMemo(() => {
    if (!payrollData) return '';
    if (payrollData.modification_deadline_display) {
      return payrollData.modification_deadline_display;
    }
    const month = payrollData.month || payrollData.payroll_month;
    const year = payrollData.year || payrollData.payroll_year;
    if (!month || !year) return '';

    let followingYear = year;
    let followingMonth = month + 1;
    if (month === 12) {
      followingYear = year + 1;
      followingMonth = 1;
    }
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `5 ${months[followingMonth - 1]} ${followingYear}`;
  }, [payrollData]);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/v1/banks'), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setBanks(data);
        }
      } catch (err) {
        console.error("Failed to fetch banks:", err);
      }
    };
    fetchBanks();
  }, []);
  const [overriddenFields, setOverriddenFields] = useState(new Set());
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);
  const [warningDetails, setWarningDetails] = useState({});
  // PRINCIPAL_DEFER state: list of defers for this run + modal control
  const [principalDefers, setPrincipalDefers] = useState([]);
  const [deferModal, setDeferModal] = useState(null); // { employee_id, emp_name } | null
  const [deferReason, setDeferReason] = useState('');
  const [deferRefDoc, setDeferRefDoc] = useState('');
  const [deferSubmitting, setDeferSubmitting] = useState(false);
  const [penaltyRules, setPenaltyRules] = useState([]);
  const [penaltyDropdown, setPenaltyDropdown] = useState(null); // { employeeId } | null
  const [penaltySearch, setPenaltySearch] = useState('');
  const [penaltyBreakdown, setPenaltyBreakdown] = useState(null);
  const [otherAllowanceModal, setOtherAllowanceModal] = useState(null); // { employeeId, empName } | null
  const [allowanceEntries, setAllowanceEntries] = useState([{ amount: '', reason: '' }]);
  const [allowanceBreakdownModal, setAllowanceBreakdownModal] = useState(null); // { employeeId, empName, breakdown: [...] }
  const [newAllowanceType, setNewAllowanceType] = useState('');
  const [newAllowanceAmount, setNewAllowanceAmount] = useState('');
  const [customAllowanceName, setCustomAllowanceName] = useState('');
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [selectedTaxCalcEmployee, setSelectedTaxCalcEmployee] = useState(null);
  const [taxCalcData, setTaxCalcData] = useState(null);
  const [taxCalcLoading, setTaxCalcLoading] = useState(false);
  const [taxCalcError, setTaxCalcError] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportResult, setShowImportResult] = useState(false);

  const tableRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!selectedTaxCalcEmployee || !payrollData || !payrollData.payroll_month || !payrollData.payroll_year) {
      setTaxCalcData(null);
      return;
    }
    const fetchTaxCalc = async () => {
      setTaxCalcLoading(true);
      setTaxCalcError('');
      try {
        const response = await fetch(buildApiUrl(`/api/v1/income-tax/employees/${selectedTaxCalcEmployee.employeeId}/tax-summary?tax_year=2026-27&month=${payrollData.payroll_month}&year=${payrollData.payroll_year}`), {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (!response.ok) {
          const detail = await response.json().then(d => d.detail).catch(() => 'Failed to load tax details');
          throw new Error(detail);
        }
        const data = await response.json();
        setTaxCalcData(data);
      } catch (err) {
        setTaxCalcError(err.message || 'Failed to load tax calculation');
      } finally {
        setTaxCalcLoading(false);
      }
    };
    fetchTaxCalc();
  }, [selectedTaxCalcEmployee, payrollData]);

  const normalizePenaltyRule = (rule) => ({
    id: rule.id,
    name: rule.name || '',
    code: rule.code || '',
    description: rule.description || '',
    amount: Number(rule.amount || 0),
    penalty_type: rule.penalty_type || 'fixed_amount',
    payroll_impact: rule.payroll_impact || 'deduction',
    status: rule.status || 'draft'
  });

  const formatCurrency = (value) =>
    `₹${Number(value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Field definitions based on employee type
  const getPayrollFields = (runType) => {
    if (runType === 'fulltime') {
      return fulltimePayrollFields;
    } else {
      return contractualPayrollFields;
    }
  };

  // Payroll field definitions for full-time employees (NEW STRUCTURE)
  const fulltimePayrollFields = {
    employee_info: {
      title: "Employee Information",
      sticky: true,
      fields: [
        { key: 'emp_code', label: 'Emp Code', width: 120, readonly: true, sticky: true },
        { key: 'employee_name', label: 'Employee Name', width: 200, readonly: true, sticky: true },
        { key: 'designation', label: 'Designation', width: 150, readonly: true },
        { key: 'date_of_birth', label: 'DOB', width: 100, readonly: true, type: 'date' },
        { key: 'join_date', label: 'DOJ', width: 100, readonly: true, type: 'date' },
        { key: 'retirement_date', label: 'DOR', width: 100, readonly: true, type: 'date' },
        { key: 'head_code', label: 'Head', width: 160, readonly: true },
        { key: 'absent_days', label: 'Absent Days', width: 120, readonly: true },
        { key: 'bank_id', label: 'Bank Name', width: 180, type: 'bank_dropdown', editable: true },
        { key: 'lic_numbers', label: 'LIC Number', width: 160, readonly: true }
      ]
    },
    earnings: {
      title: "Earnings (₹)",
      color: "bg-green-100",
      fields: [
        { key: 'basic', label: 'Basic', width: 120, type: 'currency', editable: true },
        { key: 'da', label: 'DA', width: 100, type: 'currency', editable: true },
        { key: 'hra', label: 'HRA', width: 100, type: 'currency', editable: true },
        { key: 'cca', label: 'CCA', width: 100, type: 'currency', editable: true },
        // { key: 'grade_pay', label: 'Grade Pay', width: 120, type: 'currency', editable: true }, // Grade Pay deprecated
        { key: 'tpt', label: 'TPT', width: 100, type: 'currency', readonly: true },
        { key: 'allowance', label: 'Allowance', width: 120, type: 'currency', readonly: true, calculated: true },
        { key: 'other_allowance', label: 'Other Allow', width: 120, type: 'currency', editable: true },
        { key: 'net_earning', label: 'Net Earning', width: 120, type: 'currency', readonly: true, calculated: true },
        { key: 'recovery', label: 'Recovery', width: 100, type: 'currency', readonly: true, calculated: true },
        { key: 'total_earning', label: 'Total Earning', width: 130, type: 'currency', readonly: true, calculated: true }
      ]
    },
    deductions: {
      title: "Deductions/Recoveries (₹)",
      color: "bg-red-100",
      fields: [
        { key: 'pt', label: 'PT', width: 80, type: 'currency', editable: true },
        { key: 'pfs', label: 'PFS', width: 80, type: 'currency', editable: true },
        { key: 'gis', label: 'GIS', width: 80, type: 'currency', readonly: true },
        { key: 'pf_adv', label: 'PF Adv', width: 100, type: 'currency', editable: true },
        { key: 'vpf', label: 'VPF', width: 80, type: 'currency', editable: true },
        { key: 'festival_advance', label: 'Festival Adv', width: 120, type: 'currency', editable: true },
        // { key: 'fa_current_installment', label: 'FA Curr Inst', width: 110, type: 'text', readonly: true, autoCalculated: true },
        // { key: 'fa_recovered_installments', label: 'FA Rec Inst', width: 110, type: 'text', readonly: true, autoCalculated: true },
        // { key: 'fa_remaining_installments', label: 'FA Rem Inst', width: 110, type: 'text', readonly: true, autoCalculated: true },
        // { key: 'fa_remaining_balance', label: 'FA Rem Bal', width: 110, type: 'currency', readonly: true, autoCalculated: true },
        // { key: 'fa_status', label: 'FA Status', width: 100, type: 'text', readonly: true, autoCalculated: true },
        { key: 'others', label: 'Others', width: 100, type: 'currency', editable: true },
        { key: 'income_tax', label: 'Income Tax', width: 120, type: 'currency', editable: true },
        { key: 'lic', label: 'LIC', width: 80, type: 'currency', editable: true },
        { key: 'soc', label: 'SOC', width: 80, type: 'currency', editable: true },
        { key: 'wt', label: 'WT', width: 80, type: 'currency', editable: true },
        { key: 'cpf', label: 'CPF', width: 80, type: 'currency', editable: true },
        { key: 'license_fees', label: 'License Fees', width: 120, type: 'currency', editable: true },
        { key: 'rd', label: 'RD', width: 80, type: 'currency', editable: true },
        { key: 'bank', label: 'Bank', width: 80, type: 'currency', editable: true },
        // leave_deduction is now displayed as "Recovery" in the earnings section (additions.recovery)
        // { key: 'leave_deduction', label: 'Leave Deduc', width: 120, type: 'currency', readonly: true, autoCalculated: true }
      ]
    },
    totals: {
      title: "Totals (₹)",
      color: "bg-blue-100",
      fields: [
        { key: 'total_deductions', label: 'Total Deductions', width: 140, type: 'currency', readonly: true, calculated: true },
        { key: 'net_pay', label: 'Net Pay', width: 120, type: 'currency', readonly: true, calculated: true }
      ]
    }
  };

  // Contractual payroll field definitions
  const contractualPayrollFields = {
    employee_info: {
      title: "Employee Information",
      fields: [
        { key: 'emp_no', label: 'Emp Code', width: 120, readonly: true },
        { key: 'sec_code', label: 'Sec Code', width: 100, readonly: true },
        { key: 'emp_name', label: 'Employee Name', width: 200, readonly: true },
        { key: 'emp_sex', label: 'Sex', width: 50, readonly: true },
        { key: 'emp_desig', label: 'Designation', width: 150, readonly: true }
      ]
    },
    wage_info: {
      title: "Wage Configuration",
      color: "bg-indigo-100",
      fields: [
        { key: 'wage_type', label: 'Wage Type', width: 120, readonly: true },
        { key: 'rate_per_unit', label: 'Rate/Unit', width: 120, type: 'currency', readonly: true },
        { key: 'units_worked', label: 'Units Worked', width: 120, type: 'number', editable: true },
        { key: 'total_days', label: 'Total Days', width: 100, readonly: true },
        { key: 'present_days', label: 'Present Days', width: 120, type: 'number', editable: true },
        { key: 'absent_days', label: 'Absent Days', width: 120, type: 'number', editable: true }
      ]
    },
    earnings: {
      title: "Earnings (₹)",
      color: "bg-green-100",
      fields: [
        { key: 'basic_amount', label: 'Basic Amount', width: 140, type: 'currency', readonly: true, calculated: true },
        { key: 'overtime_amount', label: 'Overtime', width: 120, type: 'currency', editable: true },
        { key: 'holiday_wages', label: 'Holiday Wages', width: 130, type: 'currency', editable: true },
        { key: 'food_allowance', label: 'Food Allow', width: 120, type: 'currency', readonly: true },
        { key: 'travel_allowance', label: 'Travel Allow', width: 120, type: 'currency', readonly: true }
      ]
    },
    deductions: {
      title: "Deductions (₹)",
      color: "bg-red-100",
      fields: [
        { key: 'esi_deduction', label: 'ESI', width: 100, type: 'currency', readonly: true, calculated: true },
        { key: 'advance_deduction', label: 'Advance Rec', width: 120, type: 'currency', readonly: true, autoCalculated: true },
        { key: 'loan_deduction', label: 'Loan Rec', width: 120, type: 'currency', readonly: true, autoCalculated: true }
      ]
    },
    totals: {
      title: "Calculated Totals (₹)",
      color: "bg-blue-100",
      fields: [
        { key: 'total_earnings', label: 'Total Earnings', width: 140, type: 'currency', readonly: true, calculated: true },
        { key: 'total_deductions', label: 'Total Deductions', width: 140, type: 'currency', readonly: true, calculated: true },
        { key: 'net_wage', label: 'Net Wage', width: 120, type: 'currency', readonly: true, calculated: true }
      ]
    }
  };

  useEffect(() => {
    if (runId) {
      fetchPayrollData();
      fetchPrincipalDefers();
      fetchPenaltyRules();
    }
  }, [runId]);

  const fetchPenaltyRules = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/v1/penalties?status=active'), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPenaltyRules((data || []).map(normalizePenaltyRule));
      }
    } catch (error) {
      console.warn('Failed to load penalty rules:', error);
    }
  };

  useEffect(() => {
    // Always fetch calculated deductions - needed for outstanding balances display
    if (employees.length > 0) {
      fetchAllCalculatedDeductions();
    }
  }, [employees.length, runId]);

  const fetchPrincipalDefers = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/loan-adjustments/defers?payroll_run_id=${runId}`), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPrincipalDefers(data.defers || []);
      }
    } catch (e) {
      console.warn('Failed to load principal defers:', e);
    }
  };

  // Helper: does this employee have an active PRINCIPAL_DEFER on this run?
  const isEmployeeDeferred = (employee_id) =>
    principalDefers.some(d => d.employee_id === employee_id && d.status !== 'reversed');

  const openDeferModal = (employee) => {
    setDeferModal({ employee_id: employee.employee_id, emp_name: employee.emp_name });
    setDeferReason('');
    setDeferRefDoc('');
  };

  const submitDefer = async () => {
    if (!deferModal || deferReason.trim().length < 3) return;
    setDeferSubmitting(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/loan-adjustments/defer-principal'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          employee_id: deferModal.employee_id,
          payroll_run_id: parseInt(runId),
          reason: deferReason.trim(),
          reference_document: deferRefDoc.trim() || null,
          loan_type_code: 'P_LOAN',
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(err.detail || 'Failed to create defer');
      }
      setDeferModal(null);
      setDeferReason('');
      setDeferRefDoc('');
      await fetchPrincipalDefers();
      setSuccess(`Principal deferment created for ${deferModal.emp_name}`);
    } catch (e) {
      setError(e.message || 'Failed to defer principal');
    } finally {
      setDeferSubmitting(false);
    }
  };

  useEffect(() => {
    if (Object.keys(calculatedDeductions).length > 0 && employees.length > 0) {
      autoPopulateLoanInterestFields();
    }
  }, [calculatedDeductions, employees.length]);

  const transformEmployeeData = (employee) => {
    /**
     * Transform flat backend structure to frontend structure
     * Backend sends NEW STRUCTURE: { additions: { basic, da, hra, cca, grade_pay, tpt, allowance, other_allowance, net_earning, recovery, total_earning }, deductions: { pt, pfs, gis, pf_adv, vpf, festival_advance, others, income_tax, lic, soc, wt, cpf, license_fees, rd }, total_deductions, net_pay }
     * Frontend expects: { additions: {...}, deductions: {...}, total_deductions, net_pay }
     */
    const additions = employee.additions || {};
    const deductions = employee.deductions || {};

    // Map backend additions to frontend structure
    const frontend_additions = {
      basic: additions.basic || 0,
      grade_pay: additions.grade_pay || 0,
      da: additions.da || 0,
      hra: additions.hra || 0,
      cca: additions.cca || 0,
      tpt: additions.tpt || 0,
      allowance: additions.allowance || 0,
      allowance_breakdown: additions.allowance_breakdown || [],
      other_allowance: additions.other_allowance || 0,
      net_earning: additions.net_earning || 0,
      recovery: additions.recovery || 0,
      total_earning: additions.total_earning || 0
    };

    // Map backend deductions to frontend structure
    const frontend_deductions = {
      pt: deductions.pt || 0,
      pfs: deductions.pfs || 0,
      gis: deductions.gis || 0,
      pf_adv: deductions.pf_adv || 0,
      vpf: deductions.vpf || 0,
      festival_advance: deductions.festival_advance || 0,
      others: deductions.others || 0,
      income_tax: deductions.income_tax || 0,
      lic: deductions.lic || 0,
      soc: deductions.soc || 0,
      wt: deductions.wt || 0,
      cpf: deductions.cpf || 0,
      license_fees: deductions.license_fees || 0,
      rd: deductions.rd || 0,
      bank: deductions.bank || 0,
      leave_deduction: deductions.leave_deduction || 0,
      penalty_deduction: deductions.penalty_deduction || 0,
      penalty_breakdown: deductions.penalty_breakdown || []
    };

    // Auto-select head_code if available_heads has only 1 option and no head_code is currently set
    const available_heads = employee.available_heads || [];
    let head_code = employee.head_code || '';
    if (!head_code && available_heads.length === 1) {
      head_code = available_heads[0];
    }

    // Return transformed employee data
    return {
      ...employee,
      head_code,
      available_heads,
      additions: frontend_additions,
      deductions: frontend_deductions,
      total_deductions: employee.total_deductions || 0,
      net_pay: employee.net_pay || 0
    };
  };

  const fetchPayrollData = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${runId}/detailed-data`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Payroll API Response", data);
        setPayrollData(data.payroll_run);

        // Transform flat backend structure to separated frontend structure
        const transformedEmployees = (data.employees || []).map(employee =>
          transformEmployeeData(employee)
        );
        console.log("Transformed employees sample:", transformedEmployees[0]);
        setEmployees(transformedEmployees);

        // Fetch overrides history trail if approved
        if (data.payroll_run?.status === 'approved') {
          try {
            const auditRes = await fetch(buildApiUrl('/api/v1/admin-override/audit-trail?entity_type=payroll&limit=200'), {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
            });
            if (auditRes.ok) {
              const auditData = await auditRes.json();
              const overridesMap = {};
              auditData.forEach(r => {
                overridesMap[`${r.employee_id}-${r.field_name}`] = true;
              });
              setDbOverrides(overridesMap);
            }
          } catch (err) {
            console.error("Failed to fetch overrides trail:", err);
          }
        }
      } else {
        throw new Error('Failed to fetch payroll data');
      }
    } catch (error) {
      setError('Failed to load payroll data');
      console.error('Error fetching payroll data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCalculatedDeductions = useCallback(async () => {
    try {
      const deductionsMap = {};
      const BATCH_SIZE = 20; // Process 20 employees at a time to avoid rate limiting

      // Split employees into batches
      for (let i = 0; i < employees.length; i += BATCH_SIZE) {
        const batch = employees.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (employee) => {
          const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${runId}/employee/${employee.employee_id}/calculated-deductions`),
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            }
          );
          if (response.ok) {
            const data = await response.json();
            return { employeeId: employee.employee_id, calculated: data.calculated_deductions };
          }
          console.error(`Failed to fetch deductions for employee ${employee.employee_id}:`, response.status);
          return null;
        });

        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach(result => {
          if (result) {
            deductionsMap[result.employeeId] = result.calculated;
          }
        });

        // Small delay between batches to be nice to the server
        if (i + BATCH_SIZE < employees.length) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      setCalculatedDeductions(deductionsMap);
    } catch (error) {
      console.error('Error fetching calculated deductions:', error);
    }
  }, [employees, runId]);

  const autoPopulateLoanInterestFields = async () => {
    // NOTE: Loan and advance fields are no longer part of BenfishSalarySlip
    // They are handled by separate loan/advance modules
    // This function is kept for compatibility but does nothing in the new structure
    console.log('Loan/advance auto-population disabled - handled by separate modules');
  };

  const calculateRowTotals = useCallback((employee) => {
    // Calculate total earnings using NEW STRUCTURE
    const totalEarnings = (
      (employee.additions?.basic || 0) +
      (employee.additions?.grade_pay || 0) +
      (employee.additions?.da || 0) +
      (employee.additions?.hra || 0) +
      (employee.additions?.cca || 0) +
      (employee.additions?.tpt || 0) +
      (employee.additions?.allowance || 0) +
      (employee.additions?.other_allowance || 0)
    );

    // Calculate total deductions using NEW STRUCTURE
    const totalDeductions = (
      (employee.deductions?.pt || 0) +
      (employee.deductions?.pfs || 0) +
      (employee.deductions?.gis || 0) +
      (employee.deductions?.pf_adv || 0) +
      (employee.deductions?.vpf || 0) +
      (employee.deductions?.festival_advance || 0) +
      (employee.deductions?.others || 0) +
      (employee.deductions?.income_tax || 0) +
      (employee.deductions?.lic || 0) +
      (employee.deductions?.soc || 0) +
      (employee.deductions?.wt || 0) +
      (employee.deductions?.cpf || 0) +
      (employee.deductions?.license_fees || 0) +
      (employee.deductions?.rd || 0) +
      (employee.deductions?.bank || 0) +
      (employee.deductions?.penalty_deduction || 0)
    );

    // Calculate net pay
    const netPay = totalEarnings - totalDeductions;

    return {
      total_earning: totalEarnings,
      total_deductions: totalDeductions,
      net_pay: netPay
    };
  }, []);

  const getSelectedPenaltyIds = (employee) =>
    new Set((employee.deductions?.penalty_breakdown || []).map((item) => item.penalty_rule_id));

  const applyPenaltySelection = async (employeeId, ruleId) => {
    const employeeIndex = employees.findIndex(emp => emp.employee_id === employeeId);
    if (employeeIndex === -1 || !payrollData) return;

    const updatedEmployees = [...employees];
    const employee = updatedEmployees[employeeIndex];
    const selected = getSelectedPenaltyIds(employee);
    if (selected.has(ruleId)) {
      selected.delete(ruleId);
    } else {
      selected.add(ruleId);
    }

    try {
      const response = await fetch(buildApiUrl('/api/v1/employee-penalties/assign'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          employee_id: employeeId,
          penalty_rule_ids: Array.from(selected),
          payroll_month: payrollData.month,
          payroll_year: payrollData.year,
          reason: 'Assigned during payroll generation'
        })
      });
      if (!response.ok) throw new Error('Failed to assign penalty');
      const result = await response.json();
      const breakdown = (result.assignments || [])
        .map((assignment) => ({
          assignment_id: assignment.id,
          penalty_rule_id: assignment.penalty_rule_id,
          name: assignment.rule?.name || 'Penalty',
          code: assignment.rule?.code || '',
          amount: assignment.penalty_amount || 0,
          penalty_type: assignment.rule?.penalty_type || 'fixed_amount',
          payroll_impact: assignment.rule?.payroll_impact || 'deduction',
          reason: assignment.reason,
          status: assignment.status
        }));
      employee.deductions = {
        ...(employee.deductions || {}),
        penalty_deduction: result.total_penalty || 0,
        penalty_breakdown: breakdown
      };
      const totals = calculateRowTotals(employee);
      employee.gross_pay = totals.gross_pay;
      employee.total_deduc = totals.total_deduc;
      employee.pay_to_ac = totals.pay_to_ac;
      setEmployees(updatedEmployees);
    } catch (error) {
      setError(error.message || 'Failed to update employee penalties');
    }
  };

  const PenaltyCell = ({ employee }) => {
    const selectedIds = getSelectedPenaltyIds(employee);
    const breakdown = employee.deductions?.penalty_breakdown || [];
    const total = employee.deductions?.penalty_deduction || 0;
    const isOpen = penaltyDropdown?.employeeId === employee.employee_id;
    const filteredRules = penaltyRules.filter((rule) =>
      [rule.name, rule.code, rule.description].some((value) => String(value).toLowerCase().includes(penaltySearch.toLowerCase()))
    );

    return (
      <div className="relative">
        <button
          type="button"
          disabled={payrollData?.status === 'approved'}
          onClick={(event) => {
            event.stopPropagation();
            setPenaltyDropdown(isOpen ? null : { employeeId: employee.employee_id });
            setPenaltySearch('');
          }}
          className="w-full flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 hover:bg-red-50 disabled:opacity-70"
          title={breakdown.map((item) => `${item.name}: ${formatCurrency(item.amount)}`).join('\n') || 'Select penalties'}
        >
          <span className="text-left">
            <span className="block text-sm font-bold text-red-700">{formatCurrency(total)}</span>
            <span className="text-xs text-gray-500">{breakdown.length} applied</span>
          </span>
          <span className="flex items-center gap-1">
            {breakdown.length > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">{breakdown.length}</span>}
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </span>
        </button>

        <div className="mt-2 flex flex-wrap gap-1">
          {breakdown.slice(0, 2).map((item) => (
            <motion.span key={item.penalty_rule_id} initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
              {item.code || item.name}
            </motion.span>
          ))}
          {breakdown.length > 2 && <span className="text-[11px] font-semibold text-gray-500">+{breakdown.length - 2}</span>}
        </div>

        {isOpen && (
          <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-3 shadow-xl">
            <input
              value={penaltySearch}
              onChange={(event) => setPenaltySearch(event.target.value)}
              placeholder="Search penalty rules"
              className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              onClick={(event) => event.stopPropagation()}
            />
            <div className="max-h-64 space-y-2 overflow-auto">
              {filteredRules.map((rule) => (
                <label key={rule.id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-100 p-3 hover:bg-red-50" title={rule.description}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(rule.id)}
                    onChange={() => applyPenaltySelection(employee.employee_id, rule.id)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-gray-900">{rule.name}</span>
                    <span className="block text-xs text-gray-500">{rule.code} • {rule.penalty_type === 'percentage_based' ? `${rule.amount}%` : formatCurrency(rule.amount)}</span>
                  </span>
                </label>
              ))}
              {filteredRules.length === 0 && <p className="py-4 text-center text-sm text-gray-500">No active penalty rules found</p>}
            </div>
            <button onClick={() => setPenaltyBreakdown(employee)} className="mt-3 w-full rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50">
              View Breakdown
            </button>
          </div>
        )}
      </div>
    );
  };

  const recalculateEmployeeSlipLocally = (employee, fieldKey, newValue, section, overrideEmpChanges = null) => {
    const dataSection = section === 'earnings' ? 'additions' : (section === 'earnings_totals' ? 'additions' : section);
    if (dataSection === 'additions' || dataSection === 'deductions' ||
      dataSection === 'loan_principals' || dataSection === 'loan_interests' || dataSection === 'advance_recoveries' ||
      dataSection === 'loan_outstanding_balances' || dataSection === 'advance_outstanding_balances') {
      if (!employee[dataSection]) employee[dataSection] = {};
      employee[dataSection][fieldKey] = newValue;
    } else {
      employee[fieldKey] = newValue;
    }

    const basic = parseFloat(employee.additions.basic) || 0;
    const grade_pay = parseFloat(employee.additions.grade_pay) || 0;
    const empChanges = overrideEmpChanges || approvedEditChanges[employee.employee_id] || {};

    let da = parseFloat(employee.additions.da) || 0;
    if (!empChanges.hasOwnProperty('da')) {
      const daPct = (typeof employee.da_percentage === 'number' ? employee.da_percentage : 18) / 100;
      da = Math.round(basic * daPct);
      employee.additions.da = da;
    }

    let hra = parseFloat(employee.additions.hra) || 0;
    if (!empChanges.hasOwnProperty('hra')) {
      if (employee.has_assigned_quarter) {
        hra = 0;
      } else {
        const hraPct = (typeof employee.hra_percentage === 'number' ? employee.hra_percentage : 12) / 100;
        hra = Math.round(basic * hraPct);
      }
      employee.additions.hra = hra;
    }

    let tpt = parseFloat(employee.additions.tpt) || 0;
    if (!empChanges.hasOwnProperty('tpt')) {
      if (employee.has_assigned_quarter) {
        tpt = 0;
      } else {
        tpt = basic < 24200 ? 1000 : 2700;
      }
      employee.additions.tpt = tpt;
    }

    const cca = parseFloat(employee.additions.cca) || 0;
    const allowance = parseFloat(employee.additions.allowance) || 0;
    const other_allowance = parseFloat(employee.additions.other_allowance) || 0;

    const net_earning = basic + da + hra + cca + grade_pay + tpt + allowance + other_allowance;
    employee.additions.net_earning = net_earning;

    const recovery = parseFloat(employee.additions.recovery) || 0;
    const total_earning = net_earning - recovery;
    employee.additions.total_earning = total_earning;

    if (!empChanges.hasOwnProperty('pfs')) {
      employee.deductions.pfs = Math.round(basic * 0.12);
    }

    let total_deductions = 0;
    Object.keys(employee.deductions).forEach(k => {
      total_deductions += parseFloat(employee.deductions[k]) || 0;
    });
    employee.total_deductions = total_deductions;
    employee.net_pay = Math.max(0, total_earning - total_deductions);
  };

  const handleCellEdit = async (employeeId, fieldKey, value, section) => {
    try {
      const employeeIndex = employees.findIndex(emp => emp.employee_id === employeeId);
      if (employeeIndex === -1) {
        console.error('Employee not found:', employeeId);
        return;
      }
      const employee = employees[employeeIndex];
      const currentPayrollFields = getPayrollFields(payrollData?.run_type);
      const fieldDef = currentPayrollFields[section]?.fields.find(f => f.key === fieldKey);

      // Handle approved edit local overrides instead of auto-save API calls
      if (payrollData?.status === 'approved' && isApprovedEditMode) {
        const floatVal = parseFloat(value) || 0;
        let newEmpChanges = {};
        setApprovedEditChanges(prev => {
          const empChanges = prev[employeeId] || {};
          newEmpChanges = {
            ...empChanges,
            [fieldKey]: floatVal
          };
          return {
            ...prev,
            [employeeId]: newEmpChanges
          };
        });
        const updatedEmployees = [...employees];
        recalculateEmployeeSlipLocally(updatedEmployees[employeeIndex], fieldKey, floatVal, section, newEmpChanges);
        setEmployees(updatedEmployees);
        return;
      }

      if (fieldDef?.autoCalculated) {
        const calculated = calculatedDeductions[employee.employee_id]?.[fieldKey] || 0;
        const newValue = parseFloat(value) || 0;

        if (Math.abs(newValue - calculated) > 0.01) {
          setWarningDetails({
            employeeId: employee.employee_id,
            employeeName: employee.emp_name,
            fieldKey,
            fieldLabel: fieldDef.label,
            originalValue: newValue,
            calculatedValue: calculated
          });
          setShowOverrideWarning(true);
          return;
        }
      }

      await saveCellEdit(employeeId, fieldKey, value, section);
    } catch (error) {
      setError(`Failed to update field: ${error.message}`);
      fetchPayrollData();
    }
  };

  const saveCellEdit = async (employeeId, fieldKey, value, section, extraFields = {}) => {
    try {
      // Find employee by ID to handle sorted/filtered arrays correctly
      const employeeIndex = employees.findIndex(emp => emp.employee_id === employeeId);
      if (employeeIndex === -1) {
        console.error('Employee not found for save:', employeeId);
        return;
      }
      // Update local state immediately for responsiveness
      const updatedEmployees = [...employees];
      const employee = updatedEmployees[employeeIndex];

      // Map 'earnings' section to 'additions' for backend compatibility
      const dataSection = section === 'earnings' ? 'additions' : section;

      if (dataSection === 'additions' || dataSection === 'deductions' ||
        dataSection === 'loan_principals' || dataSection === 'loan_interests' || dataSection === 'advance_recoveries' ||
        dataSection === 'loan_outstanding_balances' || dataSection === 'advance_outstanding_balances') {
        if (!employee[dataSection]) employee[dataSection] = {};
        employee[dataSection][fieldKey] = parseFloat(value) || 0;

        // Save extra fields locally
        Object.entries(extraFields).forEach(([k, v]) => {
          employee[dataSection][k] = v;
        });
      } else {
        employee[fieldKey] = value;
      }

      // Track override if this is an auto-calculated field
      const currentPayrollFields = getPayrollFields(payrollData?.run_type);
      const fieldDef = currentPayrollFields[section]?.fields.find(f => f.key === fieldKey);
      if (fieldDef?.autoCalculated) {
        const calculated = calculatedDeductions[employee.employee_id]?.[fieldKey] || 0;
        const newValue = parseFloat(value) || 0;
        const overrideKey = `${employee.employee_id}-${fieldKey}`;

        if (Math.abs(newValue - calculated) > 0.01) {
          setOverriddenFields(prev => new Set(prev).add(overrideKey));
        } else {
          setOverriddenFields(prev => {
            const updated = new Set(prev);
            updated.delete(overrideKey);
            return updated;
          });
        }
      }

      // Recalculate totals
      const totals = calculateRowTotals(employee);
      employee.total_earning = totals.total_earning;
      employee.total_deductions = totals.total_deductions;
      employee.net_pay = totals.net_pay;

      setEmployees(updatedEmployees);

      // Save to backend
      const updateData = {};
      if (dataSection === 'additions' || dataSection === 'deductions' ||
        dataSection === 'loan_principals' || dataSection === 'loan_interests' || dataSection === 'advance_recoveries' ||
        dataSection === 'loan_outstanding_balances' || dataSection === 'advance_outstanding_balances') {
        updateData[fieldKey] = parseFloat(value) || 0;

        // Save extra fields to backend payload
        Object.entries(extraFields).forEach(([k, v]) => {
          updateData[k] = v;
        });
      } else {
        updateData[fieldKey] = value === "" ? null : value;
      }

      const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${runId}/employee/${employee.employee_id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error('Failed to update payroll data');
      }

      const result = await response.json();

      // Update with server response
      employee.total_earning = result.total_earning;
      employee.total_deductions = result.total_deductions;
      employee.net_pay = result.net_pay;

      if (result.other_allowance !== undefined) {
        employee.additions.other_allowance = result.other_allowance;
      }
      if (result.other_allowance_details !== undefined) {
        employee.additions.other_allowance_details = result.other_allowance_details;
      }
      if (result.allowance !== undefined) {
        employee.additions.allowance = result.allowance;
      }
      if (result.allowance_breakdown !== undefined) {
        employee.additions.allowance_breakdown = result.allowance_breakdown;
      }

      setEmployees([...updatedEmployees]);

      // Auto-select the row when any cell is edited (using employee_id)
      setSelectedRows(prevSelected => {
        const newSelected = new Set(prevSelected);
        newSelected.add(employee.employee_id);
        return newSelected;
      });

    } catch (error) {
      setError(`Failed to update field: ${error.message}`);
      // Refresh data to restore correct state
      fetchPayrollData();
    }
  };

  const confirmOverride = async () => {
    const { employeeId, fieldKey, originalValue } = warningDetails;
    const currentPayrollFields = getPayrollFields(payrollData?.run_type);
    const section = Object.keys(currentPayrollFields).find(sectionKey =>
      currentPayrollFields[sectionKey].fields.some(f => f.key === fieldKey)
    );

    await saveCellEdit(employeeId, fieldKey, originalValue, section);
    setShowOverrideWarning(false);
    setWarningDetails({});
  };

  const restoreCalculatedValue = (employeeId, fieldKey) => {
    const calculatedValue = calculatedDeductions[employeeId]?.[fieldKey] || 0;
    const currentPayrollFields = getPayrollFields(payrollData?.run_type);
    const section = Object.keys(currentPayrollFields).find(sectionKey =>
      currentPayrollFields[sectionKey].fields.some(f => f.key === fieldKey)
    );

    saveCellEdit(employeeId, fieldKey, calculatedValue, section);
  };

  const openOtherAllowanceModal = (employee) => {
    const details = employee.additions?.other_allowance_details || [];
    const entries = details.length > 0
      ? details.map(d => ({
          ...d,
          amount: String(d.amount !== undefined && d.amount !== null ? d.amount : ''),
          reason: d.reason || ''
        }))
      : [{ amount: '', reason: '' }];

    setOtherAllowanceModal({
      employeeId: employee.employee_id,
      empName: employee.employee_name || employee.emp_name
    });
    setAllowanceEntries(entries);
  };

  const handleSaveOtherAllowances = async () => {
    if (!otherAllowanceModal) return;

    for (let i = 0; i < allowanceEntries.length; i++) {
      const entry = allowanceEntries[i];
      const amt = parseFloat(entry.amount);
      if (!isNaN(amt) && amt > 0) {
        if (!entry.reason || entry.reason.trim() === '') {
          alert(`Reason is mandatory for Allowance Entry ${i + 1}`);
          return;
        }
      }
    }

    const cleanEntries = allowanceEntries
      .map(e => ({
        ...e,
        amount: parseFloat(e.amount) || 0,
        reason: e.reason ? e.reason.trim() : ''
      }))
      .filter(e => e.amount > 0);

    const totalSum = cleanEntries.reduce((sum, e) => sum + e.amount, 0);

    await saveCellEdit(
      otherAllowanceModal.employeeId,
      'other_allowance',
      totalSum,
      'earnings',
      { other_allowance_details: cleanEntries }
    );

    setOtherAllowanceModal(null);
  };

  const ALLOWANCE_TYPES = [
    { type: 'washing_allowance', name: 'Washing Allowance' },
    { type: 'family_planning_allowance', name: 'Family Planning Allowance' },
    { type: 'conveyance_allowance', name: 'Conveyance Allowance' },
    { type: 'special_allowance', name: 'Special Allowance' },
    { type: 'medical_allowance', name: 'Medical Allowance' },
    { type: 'custom_allowance', name: 'Custom Allowance' }
  ];

  const openAllowanceBreakdownModal = (employee) => {
    const breakdown = employee.additions?.allowance_breakdown || [];
    setAllowanceBreakdownModal({
      employeeId: employee.employee_id,
      empName: employee.employee_name || employee.emp_name,
      breakdown: breakdown.map(item => ({ ...item }))
    });
    setNewAllowanceType('');
    setNewAllowanceAmount('');
    setCustomAllowanceName('');
    setEditingRowIndex(null);
  };

  const handleAddAllowance = () => {
    if (!newAllowanceType) return;
    const amountVal = parseFloat(newAllowanceAmount);
    if (isNaN(amountVal) || amountVal <= 0) {
      alert("Please enter a valid amount greater than 0");
      return;
    }

    let typeKey = newAllowanceType;
    let nameVal = ALLOWANCE_TYPES.find(t => t.type === newAllowanceType)?.name || '';

    if (newAllowanceType === 'custom_allowance') {
      if (!customAllowanceName.trim()) {
        alert("Please enter a custom allowance name");
        return;
      }
      typeKey = customAllowanceName.trim().toLowerCase().replace(/\s+/g, '_') + '_allowance';
      nameVal = customAllowanceName.trim();
    }

    if (allowanceBreakdownModal.breakdown.some(item => item.type === typeKey)) {
      alert("This allowance type is already added for this employee.");
      return;
    }

    const updatedBreakdown = [
      ...allowanceBreakdownModal.breakdown,
      { type: typeKey, name: nameVal, amount: amountVal }
    ];

    setAllowanceBreakdownModal({
      ...allowanceBreakdownModal,
      breakdown: updatedBreakdown
    });

    setNewAllowanceType('');
    setNewAllowanceAmount('');
    setCustomAllowanceName('');
  };

  const handleSaveAllowanceBreakdown = async () => {
    if (!allowanceBreakdownModal) return;

    const totalSum = allowanceBreakdownModal.breakdown.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    await saveCellEdit(
      allowanceBreakdownModal.employeeId,
      'allowance',
      totalSum,
      'earnings',
      { allowance_breakdown: allowanceBreakdownModal.breakdown }
    );

    setAllowanceBreakdownModal(null);
  };

  const handleBulkUpdate = async () => {
    try {
      setSaving(true);

      // Find employees by their IDs (selectedRows now contains employee_ids)
      const bulkData = Array.from(selectedRows).map(employeeId => {
        const employee = employees.find(emp => emp.employee_id === employeeId);
        return employee ? {
          employee_id: employee.employee_id,
          data: {
            // Include all editable fields from additions (earnings) and deductions
            ...employee.additions,
            ...employee.deductions
          }
        } : null;
      }).filter(Boolean);

      const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${runId}/bulk-update`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(bulkData)
      });

      if (response.ok) {
        const result = await response.json();
        setSuccess(`Bulk update completed! ${result.updated_count} records updated.`);
        setSelectedRows(new Set());
        await fetchPayrollData();
      } else {
        throw new Error('Bulk update failed');
      }
    } catch (error) {
      setError(`Bulk update failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleApprovePayroll = async () => {
    try {
      setSaving(true);
      console.log('Sending POST request to approve payroll run:', runId);
      const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${runId}/approve`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      console.log('Response status:', response.status, 'Method used:', response.request?.method);
      if (response.ok) {
        const result = await response.json();
        setSuccess(`Payroll approved successfully! Total: ₹${result.total_net_pay?.toLocaleString()}`);
        await fetchPayrollData();

        // Close editor and refresh parent after approval
        setTimeout(() => {
          onClose();
        }, 2000); // Give user time to see success message
      } else {
        const errorText = await response.text();
        console.error('Approval failed:', response.status, errorText);
        throw new Error(`Failed to approve payroll: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      setError(`Failed to approve payroll: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleExportPayroll = () => {
    if (!employees || employees.length === 0) return;

    const currentPayrollFields = getPayrollFields(payrollData?.run_type);

    // Create rows
    const exportData = employees.map(emp => {
      const row = {};
      Object.entries(currentPayrollFields).forEach(([sectionKey, section]) => {
        section.fields.forEach(field => {
          let value = '';
          if (payrollData?.run_type === 'fulltime') {
            const dataSection = sectionKey === 'earnings' ? 'additions' : sectionKey;
            if (['additions', 'deductions', 'loan_principals', 'loan_interests', 'advance_recoveries', 'loan_outstanding_balances', 'advance_outstanding_balances'].includes(dataSection)) {
              value = emp[dataSection]?.[field.key];
            } else if (sectionKey === 'totals') {
              value = emp[field.key];
            } else {
              value = emp[field.key];
            }
          } else {
            if (sectionKey === 'wage_info') {
              value = emp.wage_info?.[field.key] || emp[field.key];
            } else if (sectionKey === 'earnings' || sectionKey === 'deductions') {
              value = emp[sectionKey]?.[field.key];
            } else if (sectionKey === 'totals') {
              value = emp[field.key];
            } else {
              value = emp[field.key];
            }
          }

          if (value === undefined || value === null) {
            value = '';
          } else if (field.type === 'currency' || field.type === 'number') {
            value = parseFloat(value) || 0;
          } else if (field.type === 'date' && value && typeof value === 'string' && value.includes('-')) {
            value = value.split('T')[0].split('-').reverse().join('-');
          }

          row[field.label] = value;
        });
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll Data");
    XLSX.writeFile(workbook, `Payroll_${payrollData?.month}_${payrollData?.year}.xlsx`);
  };

  const handleImportPayroll = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportLoading(true);
    setError('');

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      if (rows.length === 0) throw new Error("The uploaded Excel file is empty.");

      // Ensure 'Emp Code' exists
      if (!Object.keys(rows[0]).some(k => k.trim() === 'Emp Code')) {
        throw new Error("Missing 'Emp Code' column in Excel file.");
      }

      const currentPayrollFields = getPayrollFields(payrollData?.run_type);

      // Build a reverse mapping from label -> { sectionKey, fieldKey, type, readonly }
      const labelToField = {};
      Object.entries(currentPayrollFields).forEach(([sectionKey, section]) => {
        section.fields.forEach(field => {
          labelToField[field.label] = { ...field, sectionKey };
        });
      });

      const bulkData = [];
      let updatedCount = 0;
      let skippedCount = 0;
      let fieldsUpdatedCount = 0;
      const importErrors = [];
      const duplicateCheck = new Set();

      rows.forEach((row, index) => {
        const rowNum = index + 2; // +1 for 0-index, +1 for header
        const empCode = row['Emp Code']?.toString().trim();

        if (!empCode) {
          importErrors.push({ row: rowNum, error: "Empty Emp Code" });
          return;
        }

        if (duplicateCheck.has(empCode)) {
          importErrors.push({ row: rowNum, error: `Duplicate Emp Code: ${empCode}` });
          return;
        }
        duplicateCheck.add(empCode);

        const emp = employees.find(e => (e.emp_code || e.emp_no) === empCode);
        if (!emp) {
          importErrors.push({ row: rowNum, error: `Employee Code ${empCode} not found in this payroll run.` });
          return;
        }

        const changes = {};

        // Diff logic
        Object.keys(row).forEach(header => {
          const fieldDef = labelToField[header.trim()];
          if (!fieldDef) return; // Ignore unknown columns

          if (fieldDef.readonly || fieldDef.calculated || fieldDef.autoCalculated) return; // Don't update readonly/calculated

          let excelVal = row[header];
          let currentVal;

          if (payrollData?.run_type === 'fulltime') {
            const dataSection = fieldDef.sectionKey === 'earnings' ? 'additions' : fieldDef.sectionKey;
            if (['additions', 'deductions', 'loan_principals', 'loan_interests', 'advance_recoveries', 'loan_outstanding_balances', 'advance_outstanding_balances'].includes(dataSection)) {
              currentVal = emp[dataSection]?.[fieldDef.key];
            } else {
              currentVal = emp[fieldDef.key];
            }
          } else {
            if (fieldDef.sectionKey === 'wage_info') {
              currentVal = emp.wage_info?.[fieldDef.key] || emp[fieldDef.key];
            } else if (fieldDef.sectionKey === 'earnings' || fieldDef.sectionKey === 'deductions') {
              currentVal = emp[fieldDef.sectionKey]?.[fieldDef.key];
            } else {
              currentVal = emp[fieldDef.key];
            }
          }

          if (fieldDef.type === 'currency' || fieldDef.type === 'number') {
            excelVal = excelVal === "" ? 0 : parseFloat(excelVal);
            currentVal = currentVal === "" || currentVal == null ? 0 : parseFloat(currentVal);

            if (isNaN(excelVal)) {
              importErrors.push({ row: rowNum, error: `Invalid number '${row[header]}' for ${header}` });
              excelVal = currentVal; // revert
            }

            // Allow precision difference
            if (Math.abs(excelVal - currentVal) > 0.01) {
              changes[fieldDef.key] = excelVal;
            }
          } else {
            // Strings / dates
            excelVal = excelVal === "" ? null : excelVal.toString();
            currentVal = currentVal === "" ? null : currentVal?.toString();

            if (excelVal !== currentVal) {
              changes[fieldDef.key] = excelVal;
            }
          }
        });

        if (Object.keys(changes).length > 0) {
          bulkData.push({
            employee_id: emp.employee_id || emp.id,
            data: changes
          });
          updatedCount++;
          fieldsUpdatedCount += Object.keys(changes).length;
        } else {
          skippedCount++;
        }
      });

      // Stop if there are validation errors and no updates
      if (importErrors.length > 0 && bulkData.length === 0) {
        setImportResult({ total: rows.length, updated: 0, skipped: skippedCount, fields: 0, errors: importErrors });
        setShowImportResult(true);
        setImportLoading(false);
        e.target.value = null;
        return;
      }

      // Send updates to the backend
      if (bulkData.length > 0) {
        const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${runId}/bulk-update`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(bulkData)
        });

        const resData = await response.json();

        if (!response.ok) {
          throw new Error(resData.detail || "Bulk update failed");
        }

        setImportResult({
          total: rows.length,
          updated: resData.updated_count,
          skipped: resData.skipped_count || skippedCount,
          fields: resData.fields_updated || fieldsUpdatedCount,
          errors: [...importErrors, ...(resData.errors || []).map(err => ({ row: 'Backend', error: JSON.stringify(err) }))]
        });

        fetchPayrollData();
      } else {
        setImportResult({ total: rows.length, updated: 0, skipped: skippedCount, fields: 0, errors: importErrors });
      }

      setShowImportResult(true);

    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImportLoading(false);
      e.target.value = null;
    }
  };

  const downloadErrorLog = () => {
    if (!importResult || !importResult.errors || importResult.errors.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(importResult.errors);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");
    XLSX.writeFile(workbook, `Import_Errors_${new Date().getTime()}.xlsx`);
  };

  const handleDeletePayroll = async () => {
    if (!window.confirm("Are you sure you want to delete this payroll run? This action cannot be undone.")) {
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${runId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        setSuccess('Payroll run deleted successfully.');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        let errorMessage = 'Failed to delete payroll';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = await response.text();
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      setError(`Failed to delete payroll: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Extract unique departments from employee data
  const uniqueDepartments = React.useMemo(() => {
    const departmentsMap = new Map();
    employees.forEach(emp => {
      if (emp.department_name) {
        departmentsMap.set(emp.department_name, emp.department_name);
      }
    });
    return Array.from(departmentsMap.keys()).sort();
  }, [employees]);

  // Sort employees by emp_code (employee number) for consistent ordering
  const sortedEmployees = React.useMemo(() => {
    return [...employees].sort((a, b) => {
      const codeA = a.emp_no || '';
      const codeB = b.emp_no || '';
      return codeA.localeCompare(codeB, undefined, { numeric: true });
    });
  }, [employees]);

  const filteredEmployees = sortedEmployees.filter(emp => {
    const searchLower = searchTerm.toLowerCase().trim();
    const matchesSearch = !searchLower ||
      emp.emp_code?.toLowerCase().includes(searchLower) ||
      emp.employee_name?.toLowerCase().includes(searchLower) ||
      emp.designation?.toLowerCase().includes(searchLower) ||
      emp.head_code?.toLowerCase().includes(searchLower);

    const matchesDepartment = !filterDepartment || emp.department_name === filterDepartment;

    return matchesSearch && matchesDepartment;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading payroll data...</span>
      </div>
    );
  }

  return (
    <div id="payroll-editor-section" className="max-w-full mx-auto p-3 sm:p-5 flex flex-col scroll-mt-20">
      {/* Header */}
      <div className="mb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {onClose && (
              <button
                onClick={onClose}
                className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 shadow-sm"
                title="Close Payroll Editor"
              >
                <X className="w-5 h-5" />
              </button>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Payroll Editor - {payrollData?.month}/{payrollData?.year}
                </h1>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase flex items-center gap-1 ${
                  payrollData?.status === 'approved'
                    ? (isPayrollLocked
                        ? 'bg-amber-100 text-amber-800 border border-amber-300'
                        : 'bg-green-100 text-green-800 border border-green-200')
                    : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                }`}>
                  {payrollData?.status === 'approved' && isPayrollLocked && <Lock className="w-3 h-3 text-amber-700" />}
                  {payrollData?.status === 'approved' && isPayrollLocked ? 'Approved (Locked)' : (payrollData?.status || 'Draft')}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-gray-600">
                Excel-like editor for all payroll fields • {employees.length} employees
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {payrollData?.status !== 'approved' && canImport && (
              <>
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleImportPayroll}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importLoading}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {importLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
                  Import
                </button>
              </>
            )}
            {canExport && (
              <button
                onClick={handleExportPayroll}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            )}
            {/* <button
              onClick={() => {
                fetchAllCalculatedDeductions();
                setTimeout(() => autoPopulateLoanInterestFields(), 500);
              }}
              className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Loan Data
            </button> */}
            {overriddenFields.size > 0 && (
              <button
                onClick={() => {
                  setOverriddenFields(new Set());
                  fetchAllCalculatedDeductions();
                }}
                className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset Overrides ({overriddenFields.size})
              </button>
            )}
            {!hideApprovalActions && payrollData?.status !== 'approved' && canDelete && (
              <button
                onClick={handleDeletePayroll}
                disabled={saving}
                className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            )}
            {payrollData?.status === 'approved' && !isPayrollLocked && canApprovedEdit && (
              isApprovedEditMode ? (
                <>
                  <button
                    onClick={() => {
                      if (Object.keys(approvedEditChanges).length === 0) {
                        setIsApprovedEditMode(false);
                        return;
                      }
                      setShowOverrideReasonModal(true);
                    }}
                    disabled={saving}
                    className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </button>
                  <button
                    onClick={() => {
                      setIsApprovedEditMode(false);
                      setApprovedEditChanges({});
                      fetchPayrollData();
                    }}
                    disabled={saving}
                    className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsApprovedEditMode(true)}
                  className="px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Approved Payroll
                </button>
              )
            )}
            {payrollData?.status === 'approved' && !isPayrollLocked && !isApprovedEditMode && canApprovedReject && (
              <button
                onClick={async () => {
                  if (!window.confirm("Are you sure you want to reject this approved payroll run? This will revert it back to draft status and unlock associated attendance, PF contributions, and tax calculation records.")) {
                    return;
                  }
                  try {
                    setSaving(true);
                    setError('');
                    const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${runId}/reject`), {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                      }
                    });
                    if (response.ok) {
                      setSuccess('Payroll run rejected and returned to draft status successfully.');
                      setTimeout(() => {
                        onClose();
                      }, 1500);
                    } else {
                      const errText = await response.text();
                      throw new Error(errText || 'Failed to reject payroll run');
                    }
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Reject Approved Payroll
              </button>
            )}
            {!hideApprovalActions && payrollData?.status !== 'approved' && canApprove && (
              <button
                onClick={handleApprovePayroll}
                disabled={saving || payrollData?.status === 'approved'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {payrollData?.status === 'approved' ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Approved
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Approve Payroll
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modification Deadline Lock Banner */}
      {payrollData?.status === 'approved' && isPayrollLocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 flex items-center text-amber-900 shadow-sm">
          <Lock className="w-5 h-5 text-amber-600 mr-2.5 flex-shrink-0" />
          <div className="text-sm font-medium">
            This payroll is locked. Editing and deletion were permitted only until {deadlineDisplay}.
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border p-2.5 sm:p-3 mb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center space-x-2">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48 sm:w-64 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="">All Departments</option>
                {uniqueDepartments.map(department => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <span className="text-xs sm:text-sm text-gray-600">
              {selectedRows.size} selected of {filteredEmployees.length} employees
            </span>
            {selectedRows.size > 0 && (
              <button
                onClick={handleBulkUpdate}
                disabled={saving}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                {saving ? 'Saving...' : 'Bulk Save'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Excel-like Payroll Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden flex flex-col min-h-0">
        <div
          className="overflow-x-auto overflow-y-auto"
          ref={tableRef}
          style={{
            position: 'relative',
            height: 'calc(100dvh - 240px)',
            minHeight: '340px',
            maxHeight: 'calc(100dvh - 180px)',
          }}
        >
          <table className="min-w-full border-collapse" style={{ tableLayout: 'fixed' }}>
            {/* Table Header - Sticky */}
            <thead className="sticky top-0 z-20 shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
              <tr className="bg-gray-100 border-b">
                {/* Checkbox column - sticky left */}
                <th className="sticky left-0 z-30 bg-gray-100 px-3 py-3 text-left border-r" style={{ width: 52, minWidth: 52, maxWidth: 52 }}>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRows(new Set(filteredEmployees.map(emp => emp.employee_id)));
                      } else {
                        setSelectedRows(new Set());
                      }
                    }}
                    checked={selectedRows.size === filteredEmployees.length && filteredEmployees.length > 0}
                  />
                </th>

                {/* Dynamic Headers */}
                {Object.entries(getPayrollFields(payrollData?.run_type)).map(([sectionKey, section]) =>
                  section.fields.map((field, fieldIndex) => {
                    // First 3 columns of employee_info should be sticky (emp_code, sec_code, emp_name)
                    const isSticky = sectionKey === 'employee_info' && fieldIndex < 3;
                    // Sticky column widths: checkbox(52) + emp_code(120) + sec_code(100) + emp_name(200)
                    const stickyLeftOffsets = [52, 172, 272]; // checkbox=52, +120=172, +100=272

                    return (
                      <th
                        key={field.key}
                        className={`px-2 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r ${section.color || 'bg-gray-100'} ${isSticky ? 'sticky z-30' : ''}`}
                        style={{
                          width: field.width,
                          minWidth: field.width,
                          maxWidth: field.width,
                          ...(isSticky && { left: stickyLeftOffsets[fieldIndex], backgroundColor: '#f3f4f6' })
                        }}
                      >
                        <div className="flex flex-col">
                          <span>{field.label}</span>
                          {/* {sectionKey !== 'employee_info' && (
                            <span className="text-xs font-normal text-gray-400 capitalize">
                              {section.title}
                            </span>
                          )} */}
                        </div>
                      </th>
                    );
                  })
                )}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEmployees.map((employee, employeeIndex) => (
                <tr key={employee.id} className="hover:bg-gray-50">
                  {/* Row Selection - sticky left */}
                  <td className="sticky left-0 z-10 bg-white px-3 py-3 border-r" style={{ width: 52, minWidth: 52, maxWidth: 52 }}>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(employee.employee_id)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedRows);
                        if (e.target.checked) {
                          newSelected.add(employee.employee_id);
                        } else {
                          newSelected.delete(employee.employee_id);
                        }
                        setSelectedRows(newSelected);
                      }}
                    />
                  </td>

                  {/* Dynamic Cells */}
                  {Object.entries(getPayrollFields(payrollData?.run_type)).map(([sectionKey, section]) =>
                    section.fields.map((field, fieldIndex) => {
                      // First 3 columns of employee_info should be sticky
                      const isSticky = sectionKey === 'employee_info' && fieldIndex < 3;
                      // Sticky column offsets: checkbox(52) + emp_code(120) + sec_code(100)
                      const stickyLeftOffsets = [52, 172, 272];
                      let cellValue;
                      if (payrollData?.run_type === 'fulltime') {
                        // Handle fulltime employee fields
                        if (sectionKey === 'earnings' || sectionKey === 'deductions' ||
                          sectionKey === 'loan_principals' || sectionKey === 'loan_interests' || sectionKey === 'advance_recoveries' ||
                          sectionKey === 'loan_outstanding_balances' || sectionKey === 'advance_outstanding_balances') {
                          // Map 'earnings' section to 'additions' for backend compatibility
                          const dataSection = sectionKey === 'earnings' ? 'additions' : sectionKey;
                          const val = field.type === 'penalties'
                            ? employee.deductions?.penalty_deduction || 0
                            : employee[dataSection]?.[field.key];
                          cellValue = val !== undefined ? val : calculatedDeductions[employee.employee_id]?.[field.key];
                          if (cellValue === undefined) {
                            cellValue = field.type === 'currency' ? 0 : '';
                          }
                        } else if (sectionKey === 'totals') {
                          cellValue = employee[field.key] || 0;
                        } else {
                          cellValue = employee[field.key] || '';
                        }
                      } else {
                        // Handle contractual employee fields
                        if (sectionKey === 'wage_info') {
                          cellValue = employee.wage_info?.[field.key] || employee[field.key] || 0;
                        } else if (sectionKey === 'earnings' || sectionKey === 'deductions') {
                          cellValue = employee[sectionKey]?.[field.key] || 0;
                        } else if (sectionKey === 'totals') {
                          cellValue = employee[field.key] || 0;
                        } else {
                          cellValue = employee[field.key] || '';
                        }
                      }

                      // Log for debugging first row only
                      if (employeeIndex === 0 && sectionKey === 'earnings') {
                        console.log("Payroll Row cell rendering:", {
                          sectionKey,
                          fieldKey: field.key,
                          cellValue,
                          employeeAdditions: employee.additions,
                          employeeDeductions: employee.deductions
                        });
                      }

                      const isEditable = (field.editable && !field.readonly && payrollData?.status !== 'approved' && canEdit) ||
                        (payrollData?.status === 'approved' && isApprovedEditMode && field.editable && !field.readonly && canApprovedEdit);
                      const isCalculated = field.calculated;
                      const isAutoCalculated = field.autoCalculated;
                      const overrideKey = `${employee.employee_id}-${field.key}`;
                      const isOverridden = overriddenFields.has(overrideKey);
                      const isLocalModified = approvedEditChanges[employee.employee_id]?.hasOwnProperty(field.key);
                      const isDbModified = dbOverrides[`${employee.employee_id}-${field.key}`];
                      const isModified = isLocalModified || isDbModified;
                      const calculatedValue = calculatedDeductions[employee.employee_id]?.[field.key] || 0;
                      const hasCalculatedValue = isAutoCalculated && calculatedValue > 0;

                      return (
                        <td
                          key={field.key}
                          className={`px-2 py-3 text-sm border-r ${section.color || ''} ${isCalculated ? 'bg-blue-50 font-semibold' : ''
                            } ${isEditable ? 'cursor-pointer hover:bg-yellow-50' : ''} ${isModified ? 'bg-yellow-100 border-l-4 border-yellow-500' : (isOverridden ? 'bg-yellow-100 border-l-4 border-yellow-500' : '')
                            } ${hasCalculatedValue && !isOverridden ? 'bg-green-50 border-l-4 border-green-500' : ''} ${isSticky ? 'sticky z-10' : ''
                            }`}
                          style={{
                            width: field.width,
                            minWidth: field.width,
                            maxWidth: field.width,
                            ...(isSticky && { left: stickyLeftOffsets[fieldIndex], backgroundColor: '#fff' })
                          }}
                          onClick={() => {
                            if (isEditable) {
                              setEditingCell({ employeeId: employee.employee_id, fieldKey: field.key, section: sectionKey });
                            }
                          }}
                        >
                          {field.type === 'penalties' ? (
                            <PenaltyCell employee={employee} />
                          ) : editingCell?.employeeId === employee.employee_id &&
                            editingCell?.fieldKey === field.key ? (
                            field.type === 'bank_dropdown' ? (
                              <select
                                defaultValue={cellValue || ""}
                                autoFocus
                                onBlur={(e) => {
                                  if (e.target.value !== String(cellValue || "")) {
                                    handleCellEdit(employee.employee_id, field.key, e.target.value, sectionKey);
                                  }
                                  setEditingCell(null);
                                }}
                                onChange={(e) => {
                                  handleCellEdit(employee.employee_id, field.key, e.target.value, sectionKey);
                                  setEditingCell(null);
                                }}
                                className="w-full p-1 border border-blue-500 rounded focus:outline-none bg-white text-sm"
                              >
                                <option value="">None</option>
                                {banks.map(bank => (
                                  bank.bank_name !== 'None' && (
                                    <option key={bank.id} value={bank.id}>
                                      {bank.bank_name}
                                    </option>
                                  )
                                ))}
                              </select>
                            ) : (
                              <input
                                type={field.type === 'currency' ? 'number' : 'text'}
                                defaultValue={cellValue}
                                autoFocus
                                onBlur={(e) => {
                                  handleCellEdit(employee.employee_id, field.key, e.target.value, sectionKey);
                                  setEditingCell(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellEdit(employee.employee_id, field.key, e.target.value, sectionKey);
                                    setEditingCell(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                className="w-full p-1 border border-blue-500 rounded focus:outline-none"
                              />
                            )
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col">
                                <span className={isModified ? 'text-yellow-800 font-semibold bg-yellow-50 px-1 border border-yellow-200 rounded' : (isOverridden ? 'text-orange-700 font-medium' : '')}>
                                  {field.type === 'currency' ?
                                    `₹${parseFloat(cellValue || 0).toLocaleString()}` :
                                    (field.type === 'bank_dropdown') ?
                                      (employee.bank_names || banks.find(b => String(b.id) === String(cellValue))?.bank_name || 'None') :
                                      (field.type === 'date' && cellValue && typeof cellValue === 'string' && cellValue.includes('-')) ?
                                        cellValue.split('T')[0].split('-').reverse().join('-') :
                                        cellValue}
                                </span>
                                {isAutoCalculated && calculatedValue !== parseFloat(cellValue || 0) && calculatedValue > 0 && payrollData?.status !== 'approved' && (
                                  <span className="text-xs text-gray-500">
                                    Auto: ₹{calculatedValue.toLocaleString()}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-1">
                                {isModified && (
                                  <span className="text-[10px] font-extrabold text-yellow-600 bg-yellow-100 border border-yellow-300 rounded px-1" title="Modified by Admin">
                                    MOD
                                  </span>
                                )}
                                {isOverridden && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      restoreCalculatedValue(employee.employee_id, field.key);
                                    }}
                                    className="text-xs px-1 py-0.5 bg-green-600 text-white rounded hover:bg-green-700"
                                    title="Restore calculated value"
                                  >
                                    ↻
                                  </button>
                                )}
                                {hasCalculatedValue && !isOverridden && payrollData?.status !== 'approved' && (
                                  <span className="text-xs text-green-600 font-medium" title="Auto-calculated">
                                    AUTO
                                  </span>
                                )}
                                {isEditable && (
                                  <Edit3 className="w-3 h-3 text-blue-400 opacity-50 hover:opacity-100 transition-opacity" title="Click to edit" />
                                )}

                                {field.key === 'allowance' && payrollData?.status !== 'approved' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openAllowanceBreakdownModal(employee);
                                    }}
                                    className="p-1 text-blue-600 hover:text-blue-800 rounded bg-blue-50 hover:bg-blue-100 flex items-center justify-center transition-colors ml-1"
                                    title="Allowance Distribution"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                )}

                                {field.key === 'other_allowance' && payrollData?.status !== 'approved' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openOtherAllowanceModal(employee);
                                    }}
                                    className="p-1 text-blue-600 hover:text-blue-800 rounded bg-blue-50 hover:bg-blue-100 flex items-center justify-center transition-colors ml-1"
                                    title="Add dynamic allowances"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {field.key === 'p_loan_principal' && payrollData?.status !== 'approved' && (
                                  isEmployeeDeferred(employee.employee_id) ? (
                                    <span
                                      className="text-[10px] px-1.5 py-0.5 bg-yellow-200 text-yellow-800 rounded font-semibold"
                                      title="Principal deferred for this payroll run (MD authorization)"
                                    >
                                      DEFERRED
                                    </span>
                                  ) : (
                                    parseFloat(cellValue || 0) > 0 && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openDeferModal(employee);
                                        }}
                                        className="text-xs px-1 py-0.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 flex items-center gap-0.5"
                                        title="Defer principal for this payroll run (requires MD authorization)"
                                      >
                                        <PauseCircle className="w-3 h-3" />
                                        Defer
                                      </button>
                                    )
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>

            {/* Summary Row */}
            {showCalculations && (
              <tfoot className="bg-gray-100 border-t-2 sticky bottom-0 z-10 shadow-[0_-1px_2px_rgba(0,0,0,0.06)]">
                <tr>
                  <td className="sticky left-0 z-20 bg-gray-100 px-3 py-3 font-semibold" style={{ width: 52, minWidth: 52, maxWidth: 52 }}>
                    TOTAL
                  </td>
                  {Object.entries(getPayrollFields(payrollData?.run_type)).map(([sectionKey, section]) =>
                    section.fields.map((field, fieldIndex) => {
                      const isSticky = sectionKey === 'employee_info' && fieldIndex < 3;
                      const stickyLeftOffsets = [52, 172, 272];
                      return (
                        <td
                          key={field.key}
                          className={`px-2 py-3 text-sm font-semibold border-r ${isSticky ? 'sticky z-20 bg-gray-100' : ''}`}
                          style={{
                            width: field.width,
                            minWidth: field.width,
                            ...(isSticky && { left: stickyLeftOffsets[fieldIndex] })
                          }}
                        >
                          {field.type === 'currency' ? (
                            `₹${employees.reduce((sum, emp) => {
                              let value = 0;
                              if (payrollData?.run_type === 'fulltime') {
                                // Map 'earnings' section to 'additions' for backend compatibility (same as row rendering)
                                const dataSection = sectionKey === 'earnings' ? 'additions' : sectionKey;
                                if (dataSection === 'additions' || dataSection === 'deductions' ||
                                  dataSection === 'loan_principals' || dataSection === 'loan_interests' || dataSection === 'advance_recoveries' ||
                                  dataSection === 'loan_outstanding_balances' || dataSection === 'advance_outstanding_balances') {
                                  value = emp[dataSection]?.[field.key] || (field.type === 'currency' ? 0 : '');
                                } else if (sectionKey === 'totals') {
                                  value = emp[field.key] || 0;
                                }
                              } else {
                                // Handle contractual employee fields
                                if (sectionKey === 'wage_info') {
                                  value = emp.wage_info?.[field.key] || emp[field.key] || 0;
                                } else if (sectionKey === 'earnings' || sectionKey === 'deductions') {
                                  value = emp[sectionKey]?.[field.key] || 0;
                                } else if (sectionKey === 'totals') {
                                  value = emp[field.key] || 0;
                                }
                              }
                              return sum + (parseFloat(value) || 0);
                            }, 0).toLocaleString()}`
                          ) : (
                            ''
                          )}
                        </td>
                      );
                    })
                  )}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Override Warning Modal */}
      <AnimatePresence>
        {showOverrideWarning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-lg p-6 max-w-md mx-4"
            >
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-orange-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Override Auto-Calculated Value?
                </h3>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-gray-700">
                  You're changing <strong>{warningDetails.fieldLabel}</strong> for <strong>{warningDetails.employeeName}</strong>
                </p>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Auto-calculated:</span>
                    <span className="font-medium text-green-600">₹{warningDetails.calculatedValue?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Your value:</span>
                    <span className="font-medium text-orange-600">₹{warningDetails.originalValue?.toLocaleString()}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  This will override the automatic loan calculation. You can restore the calculated value later.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowOverrideWarning(false);
                    setWarningDetails({});
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmOverride}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                >
                  Override Value
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Statistics */}
      <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        <div className="bg-green-50 p-3 rounded-lg border border-green-100 shadow-sm">
          <div className="flex items-center">
            <Users className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-green-700 font-medium">Total Employees</p>
              <p className="text-base sm:text-lg font-bold text-green-900 truncate">{filteredEmployees.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 shadow-sm">
          <div className="flex items-center">
            <IndianRupee className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-blue-700 font-medium">Total Gross</p>
              <p className="text-base sm:text-lg font-bold text-blue-900 truncate">
                ₹{filteredEmployees.reduce((sum, emp) => sum + (emp.additions?.total_earning || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-red-50 p-3 rounded-lg border border-red-100 shadow-sm">
          <div className="flex items-center">
            <TrendingUp className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-red-700 font-medium">Total Deductions</p>
              <p className="text-base sm:text-lg font-bold text-red-900 truncate">
                ₹{filteredEmployees.reduce((sum, emp) => sum + (emp.total_deductions || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 p-3 rounded-lg border border-purple-100 shadow-sm">
          <div className="flex items-center">
            <IndianRupee className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-purple-700 font-medium">Net Payroll</p>
              <p className="text-base sm:text-lg font-bold text-purple-900 truncate">
                ₹{(filteredEmployees.length > 0
                  ? filteredEmployees.reduce((sum, emp) => sum + (emp.net_pay || 0), 0)
                  : (payrollData?.total_net_pay || 0)
                ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        {overriddenFields.size > 0 && (
          <div className="col-span-2 lg:col-span-4 bg-orange-50 p-3 rounded-lg border border-orange-200">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0" />
              <div>
                <p className="text-xs text-orange-700 font-medium">Manual Overrides Active</p>
                <p className="text-sm font-semibold text-orange-900">{overriddenFields.size} field(s) manually adjusted</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Penalty Breakdown Modal */}
      <AnimatePresence>
        {penaltyBreakdown && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
            onClick={() => setPenaltyBreakdown(null)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 p-6"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Penalty Breakdown</h3>
                  <p className="text-sm text-gray-500">{penaltyBreakdown.emp_name}</p>
                </div>
                <button onClick={() => setPenaltyBreakdown(null)}>
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                {(penaltyBreakdown.deductions?.penalty_breakdown || []).map((item) => (
                  <div key={item.penalty_rule_id} className="flex items-center justify-between gap-4 p-4">
                    <div>
                      <p className="font-semibold text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.code} • {item.reason || 'Payroll penalty assignment'}</p>
                    </div>
                    <p className="font-bold text-red-700">{formatCurrency(item.amount)}</p>
                  </div>
                ))}
                {(penaltyBreakdown.deductions?.penalty_breakdown || []).length === 0 && (
                  <div className="p-6 text-center text-sm text-gray-500">No penalties selected for this payroll period.</div>
                )}
                <div className="flex items-center justify-between bg-red-50 p-4 font-bold text-red-800">
                  <span>Total</span>
                  <span>{formatCurrency(penaltyBreakdown.deductions?.penalty_deduction || 0)}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Principal Defer Modal */}
      <AnimatePresence>
        {deferModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
            onClick={() => !deferSubmitting && setDeferModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <PauseCircle className="w-5 h-5 text-yellow-600" />
                  Defer Principal — {deferModal.emp_name}
                </h3>
                <button onClick={() => setDeferModal(null)} disabled={deferSubmitting}>
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Skip the P_LOAN principal deduction for this payroll run only.
                Interest is still deducted. The loan schedule extends by one month.
                Must be authorized in writing by the MD.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason / MD authorization note <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={deferReason}
                    onChange={(e) => setDeferReason(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="e.g. MD authorization dated 2026-04-12 — one-month principal hold"
                    disabled={deferSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference document (optional)
                  </label>
                  <input
                    type="text"
                    value={deferRefDoc}
                    onChange={(e) => setDeferRefDoc(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="e.g. MD-AUTH-2026-042.pdf"
                    disabled={deferSubmitting}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setDeferModal(null)}
                  disabled={deferSubmitting}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitDefer}
                  disabled={deferSubmitting || deferReason.trim().length < 3}
                  className="px-4 py-2 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 flex items-center gap-1"
                >
                  {deferSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Defer Principal
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Other Allowance Modal */}
      <AnimatePresence>
        {otherAllowanceModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
            onClick={() => setOtherAllowanceModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-xl w-full mx-4 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 pb-2 border-b">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <IndianRupee className="w-5 h-5 text-blue-600" />
                  Other Allowance Details — {otherAllowanceModal.empName}
                </h3>
                <button onClick={() => setOtherAllowanceModal(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">
                  Dynamic Allowance Entries (Maximum 3)
                </p>

                <div className="max-h-[300px] overflow-y-auto space-y-3 pr-1">
                  {allowanceEntries.map((entry, index) => {
                    const isSystemGenerated = !!entry.system_generated || entry.code === 'NPA' || entry.reason === 'NPA Allowance';
                    return (
                      <div key={index} className="flex gap-2 items-start border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                        {/* Amount Textbox */}
                        <div className="w-1/3">
                          <label className="block text-xs font-semibold text-gray-500 mb-1 flex items-center justify-between">
                            <span>Amount (₹) {!isSystemGenerated && <span className="text-red-500">*</span>}</span>
                            {isSystemGenerated && (
                              <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-1 rounded border border-purple-200">System</span>
                            )}
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              value={entry.amount}
                              readOnly={isSystemGenerated}
                              onChange={(e) => {
                                if (isSystemGenerated) return;
                                const newEntries = [...allowanceEntries];
                                newEntries[index].amount = e.target.value;
                                setAllowanceEntries(newEntries);
                              }}
                              placeholder="Amount"
                              className={`w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isSystemGenerated ? 'bg-gray-100 text-gray-700 font-semibold cursor-not-allowed' : ''}`}
                            />
                          </div>
                        </div>

                        {/* Reason Textbox */}
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Reason / Description {!isSystemGenerated && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            value={entry.reason}
                            readOnly={isSystemGenerated}
                            onChange={(e) => {
                              if (isSystemGenerated) return;
                              const newEntries = [...allowanceEntries];
                              newEntries[index].reason = e.target.value;
                              setAllowanceEntries(newEntries);
                            }}
                            placeholder="e.g. Festival bonus, special service duty"
                            className={`w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isSystemGenerated ? 'bg-gray-100 text-gray-700 font-semibold cursor-not-allowed' : ''}`}
                          />
                        </div>

                        {/* Action buttons next to amount/reason fields */}
                        <div className="flex items-center gap-1 mt-6 self-start">
                          {index === allowanceEntries.length - 1 && allowanceEntries.filter(e => !e.system_generated && e.code !== 'NPA' && e.reason !== 'NPA Allowance').length < 3 && (
                            <button
                              type="button"
                              onClick={() => {
                                setAllowanceEntries([...allowanceEntries, { amount: '', reason: '' }]);
                              }}
                              className="p-1.5 text-green-600 bg-green-50 hover:bg-green-100 rounded transition-colors border border-green-200"
                              title="Add new manual row"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          )}
                          {!isSystemGenerated && allowanceEntries.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newEntries = allowanceEntries.filter((_, idx) => idx !== index);
                                setAllowanceEntries(newEntries);
                              }}
                              className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors border border-red-200"
                              title="Remove row"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-gray-50 p-3 rounded-lg flex items-center justify-between border mt-4">
                  <span className="text-sm font-semibold text-gray-700">Total Sum:</span>
                  <span className="text-base font-bold text-blue-700">
                    ₹{allowanceEntries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setOtherAllowanceModal(null)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOtherAllowances}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 transition-colors font-medium shadow-sm"
                >
                  <Save className="w-4 h-4" />
                  Save Allowances
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {selectedTaxCalcEmployee && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              className="bg-white rounded-xl shadow-xl border w-full max-w-2xl overflow-hidden"
            >
              <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
                <h3 className="font-bold text-gray-900">Tax Calculation Worksheet - {selectedTaxCalcEmployee.empName}</h3>
                <button type="button" onClick={() => setSelectedTaxCalcEmployee(null)} className="text-gray-400 hover:text-gray-600 font-semibold">✕</button>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {taxCalcLoading && <div className="text-center py-6 text-gray-500">Loading calculation worksheet...</div>}
                {taxCalcError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{taxCalcError}</div>}
                {taxCalcData && (
                  <>
                    <div className="grid grid-cols-2 gap-4 text-xs bg-gray-50 p-4 rounded border">
                      <div><span className="text-gray-500 font-medium">Financial Year:</span> <span className="font-semibold">{taxCalcData.tax_year || '2026-27'}</span></div>
                      <div><span className="text-gray-500 font-medium">Tax Regime:</span> <span className="font-semibold">{taxCalcData.regime}</span></div>
                      <div><span className="text-gray-500 font-medium">Projected Annual Income:</span> <span className="font-semibold">₹{taxCalcData.projected_annual_income?.toLocaleString()}</span></div>
                      <div><span className="text-gray-500 font-medium">Standard Deduction:</span> <span className="text-red-600 font-semibold">- ₹{taxCalcData.standard_deduction?.toLocaleString()}</span></div>
                      <div><span className="text-gray-500 font-medium">Approved Exemption Claims:</span> <span className="text-red-600 font-semibold">- ₹{taxCalcData.approved_benefits_total?.toLocaleString()}</span></div>
                      <div><span className="text-gray-500 font-medium">Net Taxable Income:</span> <span className="font-bold text-blue-900">₹{taxCalcData.taxable_income?.toLocaleString()}</span></div>
                      <div><span className="text-gray-500 font-medium">Final Annual Tax:</span> <span className="font-bold text-blue-900">₹{taxCalcData.annual_tax_liability?.toLocaleString()}</span></div>
                      <div><span className="text-gray-500 font-medium">Monthly TDS (Computed):</span> <span className="font-extrabold text-green-700">₹{taxCalcData.final_tds?.toLocaleString()}</span></div>
                    </div>

                    <div className="text-xs">
                      <p className="font-bold text-gray-900 mb-2">Detailed Slab Breakdown</p>
                      <div className="space-y-1.5 border rounded p-3">
                        {(taxCalcData.slab_calculation || []).map((slab, index) => (
                          <div key={index} className="flex justify-between text-gray-600 border-b pb-1 last:border-0 last:pb-0">
                            <span>Slab ₹{slab.min.toLocaleString()} - {slab.max ? `₹${slab.max.toLocaleString()}` : 'No limit'} ({slab.rate}%)</span>
                            <span>Taxable: ₹{slab.taxable_in_slab?.toLocaleString()} → Tax: <span className="font-semibold text-gray-900">₹{slab.tax_in_slab?.toLocaleString()}</span></span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="text-xs space-y-1">
                      <div className="flex justify-between"><span>Base Tax Before Rebate:</span><span className="font-semibold">₹{taxCalcData.tax_before_rebate?.toLocaleString()}</span></div>
                      {taxCalcData.rebate_amount > 0 && <div className="flex justify-between text-green-700"><span>Section 87A Rebate:</span><span>- ₹{taxCalcData.rebate_amount?.toLocaleString()}</span></div>}
                      {taxCalcData.surcharge_amount > 0 && <div className="flex justify-between text-red-600"><span>Surcharge:</span><span>+ ₹{taxCalcData.surcharge_amount?.toLocaleString()}</span></div>}
                      <div className="flex justify-between"><span>Health & Education Cess (4%):</span><span className="font-semibold">+ ₹{taxCalcData.cess_amount?.toLocaleString()}</span></div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
                <button type="button" onClick={() => setSelectedTaxCalcEmployee(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-semibold">Close</button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {/* Import Result Modal */}
        {showImportResult && importResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              className="bg-white rounded-xl shadow-xl border w-full max-w-lg overflow-hidden"
            >
              <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
                <h3 className="font-bold text-gray-900">
                  {importResult.errors && importResult.errors.length > 0 ? "Import Completed With Errors" : "Import Completed Successfully"}
                </h3>
                <button type="button" onClick={() => setShowImportResult(false)} className="text-gray-400 hover:text-gray-600 font-semibold">✕</button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm bg-gray-50 p-4 rounded border">
                  <div><span className="text-gray-500 font-medium">Total rows in Excel:</span> <span className="font-semibold">{importResult.total}</span></div>
                  <div><span className="text-gray-500 font-medium">Rows updated:</span> <span className="font-semibold text-green-700">{importResult.updated}</span></div>
                  <div><span className="text-gray-500 font-medium">Rows skipped (no changes):</span> <span className="font-semibold">{importResult.skipped}</span></div>
                  <div><span className="text-gray-500 font-medium">Fields updated:</span> <span className="font-semibold text-blue-700">{importResult.fields}</span></div>
                  <div><span className="text-gray-500 font-medium">Errors:</span> <span className="font-semibold text-red-600">{importResult.errors?.length || 0}</span></div>
                </div>

                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-4 border rounded border-red-200 overflow-hidden">
                    <div className="bg-red-50 px-4 py-2 border-b border-red-200 text-red-800 font-semibold text-sm flex justify-between items-center">
                      <span>Import Errors</span>
                      <button
                        onClick={downloadErrorLog}
                        className="text-xs bg-white text-red-700 px-2 py-1 rounded border border-red-300 hover:bg-red-50 flex items-center"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download Error Report
                      </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto p-0">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 border-b">Row</th>
                            <th className="px-3 py-2 border-b">Error Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.errors.map((err, i) => (
                            <tr key={i} className="border-b last:border-0 hover:bg-red-50/50">
                              <td className="px-3 py-2 align-top">{err.row}</td>
                              <td className="px-3 py-2 text-red-600">{err.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
                <button type="button" onClick={() => setShowImportResult(false)} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 text-sm font-semibold">Done</button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {/* Allowance Breakdown Modal */}
        {allowanceBreakdownModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              className="bg-white rounded-xl shadow-xl border w-full max-w-md overflow-hidden"
            >
              <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
                <h3 className="font-bold text-gray-900">Allowance Distribution</h3>
                <button type="button" onClick={() => setAllowanceBreakdownModal(null)} className="text-gray-400 hover:text-gray-600 font-semibold">✕</button>
              </div>

              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <p className="text-sm text-gray-500 font-medium mb-2">Employee: {allowanceBreakdownModal.empName}</p>

                <div className="space-y-2">
                  {allowanceBreakdownModal.breakdown.map((item, idx) => (
                    <div key={item.type || idx} className="flex items-center justify-between border-b pb-2">
                      <span className="text-sm text-gray-700 font-medium">{item.name}</span>
                      <div className="flex items-center space-x-2">
                        {editingRowIndex === idx ? (
                          <input
                            type="number"
                            value={item.amount}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const updated = [...allowanceBreakdownModal.breakdown];
                              updated[idx].amount = val;
                              setAllowanceBreakdownModal({
                                ...allowanceBreakdownModal,
                                breakdown: updated
                              });
                            }}
                            onBlur={() => setEditingRowIndex(null)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') setEditingRowIndex(null);
                            }}
                            className="w-24 px-2 py-1 border rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                            autoFocus
                          />
                        ) : (
                          <span className="text-sm font-semibold text-gray-900">₹{item.amount.toLocaleString()}</span>
                        )}

                        <button
                          type="button"
                          onClick={() => setEditingRowIndex(editingRowIndex === idx ? null : idx)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Edit"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            const updated = allowanceBreakdownModal.breakdown.filter((_, i) => i !== idx);
                            setAllowanceBreakdownModal({
                              ...allowanceBreakdownModal,
                              breakdown: updated
                            });
                          }}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {allowanceBreakdownModal.breakdown.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">No allowances added.</p>
                  )}
                </div>

                {/* Add New Allowance Form */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 space-y-3 mt-4">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">+ Add Allowance</p>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] text-gray-500 font-medium mb-1">Allowance Type</label>
                      <select
                        value={newAllowanceType}
                        onChange={(e) => setNewAllowanceType(e.target.value)}
                        className="w-full text-xs border rounded p-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Select ▼</option>
                        {ALLOWANCE_TYPES.map(t => (
                          <option key={t.type} value={t.type}>{t.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[11px] text-gray-500 font-medium mb-1">Amount</label>
                      <input
                        type="number"
                        placeholder="₹ Amount"
                        value={newAllowanceAmount}
                        onChange={(e) => setNewAllowanceAmount(e.target.value)}
                        className="w-full text-xs border rounded p-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {newAllowanceType === 'custom_allowance' && (
                    <div>
                      <label className="block text-[11px] text-gray-500 font-medium mb-1">Custom Allowance Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Special Project Allowance"
                        value={customAllowanceName}
                        onChange={(e) => setCustomAllowanceName(e.target.value)}
                        className="w-full text-xs border rounded p-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleAddAllowance}
                    disabled={!newAllowanceType || !newAllowanceAmount}
                    className="w-full py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold disabled:opacity-50 transition-colors"
                  >
                    Add
                  </button>
                </div>

                <div className="flex justify-between items-center pt-4 border-t text-sm font-bold text-gray-900">
                  <span>Total Allowance:</span>
                  <span>₹{allowanceBreakdownModal.breakdown.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toLocaleString()}</span>
                </div>
              </div>

              <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
                <button
                  type="button"
                  onClick={() => setAllowanceBreakdownModal(null)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveAllowanceBreakdown}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {showOverrideReasonModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden text-left"
            >
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-bold text-gray-900">Reason for Overrides</h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-500 mb-4">
                  Please provide a reason/remarks for modifying these approved payroll records. This will be captured in the audit trail.
                </p>
                <textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="e.g. Adjusted basic salary as per revised executive order..."
                />
              </div>
              <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
                <button
                  type="button"
                  onClick={() => setShowOverrideReasonModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!overrideReason.trim()) {
                      setError('Please provide a reason for the overrides');
                      return;
                    }
                    try {
                      setSaving(true);
                      setError('');
                      setShowOverrideReasonModal(false);
                      const employeeIds = Object.keys(approvedEditChanges);
                      for (const empId of employeeIds) {
                        const changes = approvedEditChanges[empId];
                        const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/runs/${runId}/employee/${empId}/approved-override`), {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                          },
                          body: JSON.stringify({
                            reason: overrideReason,
                            changes: changes
                          })
                        });
                        if (!response.ok) {
                          const errText = await response.text();
                          throw new Error(`Failed to save override for employee ${empId}: ${errText}`);
                        }
                      }
                      setSuccess('Approved payroll overrides saved successfully.');
                      setIsApprovedEditMode(false);
                      setApprovedEditChanges({});
                      setOverrideReason('');
                      fetchPayrollData();
                    } catch (err) {
                      setError(err.message);
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={!overrideReason.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                >
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PayrollEditor;
